import React, { useEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { Button, Dropdown } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import config from '../../config/Constant';
import { getRequest, postRequestAsync } from '../../helpers/apiHandler';
import CreateUserRateModal from './CreateUserRateModal';
import DeleteUserRateModal from './DeleteUserRateModal';
import UpdateUserRateModal from './UpdateUserRateModal';
import { titleCase } from '../../helpers/helperFunction';
import plusIcon from '../../assets/SidebarIcons/icon-plus.svg';
import ListPage from '../ListPage';

const UserRates = () => {
  const { access_token } = useSelector((state) => state.auth.user);
  const [loadingData, setLoadingData] = useState(false);
  const [search, setSearch] = useState('');
  const [data, setData] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userRateData, setUserRateData] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInput = useRef(null);

  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });

  const getCreditData = () => {
    setLoadingData(true);
    const data = { headers: { Authorization: access_token.token } };
    getRequest(`/admin/user-rates`, data, (res) => {
      if (res.success) {
        const result = res.data;
        setData(result);
        setSearchData(result);
        generateColumns(result);
      } else {
        setAlertState({
          message: 'There was an issue while fetching user credits',
          show: true,
          variant: 'danger',
        });
      }
      setLoadingData(false);
    });
  };

  const generateColumns = (data) => {
    if (data.length > 0) {
      const keys = Object.keys(data[0]);
      const generatedColumns = keys.map((key) => ({
        name: key === 'iso2' ? 'Country' : titleCase(key.replace('_', ' ')),
        sortable: true,
        selector: (row) => row[key],
      }));

      generatedColumns.push({
        name: 'Actions',
        minWidth: '250px',
        selector: (row) => (
          <div className="d-flex justify-content-center align-items-center gap-1">
            <Button
              variant="primary"
              style={{ minWidth: 100 }}
              onClick={() => {
                setUserRateData(row);
                setShowUpdateModal(true);
              }}
            >
              Edit
            </Button>
            <Button
              variant="ghost"
              style={{ minWidth: 100 }}
              onClick={() => {
                setUserRateData(row);
                setShowDeleteModal(true);
              }}
            >
              Delete
            </Button>
          </div>
        ),
      });

      setColumns(generatedColumns);
    }
  };

  const onSearch = (evt) => {
    const { value } = evt.target;
    const filteredData = data.filter(
      (item) =>
        item.name.toLowerCase().includes(value.toLowerCase()) ||
        item.email.toLowerCase().includes(value.toLowerCase())
    );

    setSearch(value);
    setSearchData(value ? filteredData : data);
  };

  // CSV columns: email,iso2,cost. Same format for export and import.
  const exportCsv = () => {
    const lines = data.map((r) => [r.email, r.iso2, String(r.cost).split(' ').pop()].join(','));
    const csv = ['email,iso2,cost', ...lines].join('\n');
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'user_rates.csv');
  };

  // Upsert by (email, iso2) via user-rates-create.
  const importCsv = async (evt) => {
    const file = evt.target.files[0];
    evt.target.value = '';
    if (!file) return;

    const rows = (await file.text())
      .split(/\r?\n/)
      .map((line, i) => ({
        line: i + 1,
        cells: line.split(',').map((c) => c.trim().replace(/^"|"$/g, '')),
      }))
      .filter(({ cells }) => cells.join('') !== '' && cells[0].toLowerCase() !== 'email');

    const errors = [];
    let created = 0;
    let updated = 0;

    setImporting(true);
    for (const { line, cells } of rows) {
      const [email, rawIso2, cost] = cells;
      const iso2 = (rawIso2 || '').toUpperCase();
      if (!email || !/^[A-Z]{2}$/.test(iso2) || cost === '' || isNaN(cost) || Number(cost) < 0) {
        errors.push(`line ${line}: invalid row`);
        continue;
      }
      // create upserts on (user, iso2) server-side
      const res = await postRequestAsync(
        '/admin/user-rates-create',
        { email, iso2, rate: cost },
        access_token.token
      );
      if (!res.success) errors.push(`line ${line}: ${res.message || 'request failed'}`);
      else if (res.data && res.data.inserted) created += 1;
      else updated += 1;
    }
    setImporting(false);

    setAlertState({
      show: true,
      variant: errors.length ? 'danger' : 'success',
      message: `Import: ${created} created, ${updated} updated${errors.length ? `, ${errors.length} failed (${errors.slice(0, 5).join('; ')})` : ''}`,
    });
    getCreditData();
  };

  useEffect(() => {
    getCreditData();
  }, []);

  useEffect(() => {
    if (alertState.show) {
      setTimeout(() => {
        setAlertState({
          message: '',
          show: false,
          variant: '',
        });
      }, config.notify_time);
    }
  }, [alertState]);

  return (
    <ListPage
      title="User Rates"
      search={search}
      onSearch={onSearch}
      alert={alertState}
      setAlert={setAlertState}
      columns={columns}
      data={searchData}
      loading={loadingData}
      actions={
        <>
          <input type="file" accept=".csv,text/csv" hidden ref={fileInput} onChange={importCsv} />
          <Dropdown>
            <Dropdown.Toggle variant="ghost" disabled={importing}>
              {importing ? 'Importing...' : 'Import / Export'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => fileInput.current.click()}>Import CSV</Dropdown.Item>
              <Dropdown.Item disabled={!data.length} onClick={exportCsv}>
                Export CSV
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Button onClick={() => setShowModal(true)}>
            <img alt="..." src={plusIcon} className="mr-2" /> Add User Rate
          </Button>
        </>
      }
    >
      <CreateUserRateModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        setAlertState={setAlertState}
        getCreditData={getCreditData}
      />
      <UpdateUserRateModal
        show={showUpdateModal}
        handleClose={() => {
          setShowUpdateModal(false);
          setUserRateData(null);
        }}
        setAlertState={setAlertState}
        getCreditData={getCreditData}
        userRateData={userRateData}
      />
      <DeleteUserRateModal
        userData={userRateData}
        handleClose={() => {
          setUserRateData(null);
          setShowDeleteModal(false);
        }}
        show={showDeleteModal}
        getCreditData={getCreditData}
        setAlertState={setAlertState}
      />
    </ListPage>
  );
};

export default UserRates;
